import type { EInvoiceTemplate } from '@/lib/api/einvoice-api'
import { SelectItem } from '@/components/ui/select'
import { m } from '@/lib/paraglide/messages'

function templateName(template: EInvoiceTemplate) {
    return (
        template.template_name || template.inv_series || template.ip_template_id
    )
}

function templateMetadata(template: EInvoiceTemplate) {
    return [
        m.invoice_misa_template_number({ number: template.inv_template_no }),
        m.invoice_misa_template_series({ series: template.inv_series }),
        template.invoice_with_code
            ? m.invoice_misa_template_with_code()
            : m.invoice_misa_template_without_code(),
        template.is_more_vat_rate
            ? m.invoice_misa_template_multi_vat()
            : m.invoice_misa_template_single_vat(),
    ].join(' · ')
}

export function InvoiceTemplateOption({
    template,
}: {
    template: EInvoiceTemplate
}) {
    const metadata = templateMetadata(template)
    const accessibleName = `${templateName(template)} ${metadata.replaceAll(' · ', ' ')}`

    return (
        <SelectItem value={template.ip_template_id} textValue={accessibleName}>
            <span className="flex min-w-0 flex-col whitespace-normal">
                <span className="font-medium">{templateName(template)}</span>
                {template.inv_template_no ? (
                    <span className="text-xs text-muted-foreground">
                        {metadata}
                    </span>
                ) : null}
            </span>
        </SelectItem>
    )
}

export function InvoiceTemplateSummary({
    template,
    series,
}: {
    template?: EInvoiceTemplate
    series: string
}) {
    return (
        <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
            <p className="font-medium">{m.invoice_misa_template_summary()}</p>
            <p className="text-muted-foreground">
                {template
                    ? templateMetadata(template)
                    : m.invoice_misa_template_series({ series })}
            </p>
        </div>
    )
}

export function InvoiceTemplateLoadStatus({
    isPending,
    hasLoaded,
    count,
    error,
    isStale,
}: {
    isPending: boolean
    hasLoaded: boolean
    count: number
    error?: { title: string; description?: string }
    isStale: boolean
}) {
    const loadedMessage = count
        ? m.invoice_misa_template_loaded({ count: String(count) })
        : m.invoice_misa_template_empty()

    return (
        <>
            <div aria-live="polite" className="text-sm text-muted-foreground">
                {isPending
                    ? m.invoice_misa_template_loading()
                    : hasLoaded
                      ? loadedMessage
                      : null}
            </div>
            {error ? (
                <p role="alert" className="text-sm text-destructive">
                    {error.title}
                    {error.description ? ` · ${error.description}` : ''}
                </p>
            ) : null}
            {isStale ? (
                <p
                    role="alert"
                    className="text-sm text-amber-700 dark:text-amber-400"
                >
                    {m.invoice_misa_template_stale()}
                </p>
            ) : null}
        </>
    )
}
